import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useTenant } from '../contexts/TenantContext'
import { soundFx } from '../lib/soundEffects'

import { isDemoTournament, getDemoMatches } from '../data/demoData'

export const useMatches = (tournamentId) => {
  const { activeTenant } = useTenant()
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isDemoTournament(tournamentId)) {
      console.log('--- DEMO MODE ACTIVATED ---')
      setMatches(getDemoMatches(tournamentId))
      setLoading(false)
      return
    }
  }, [tournamentId])

  useEffect(() => {
    if (tournamentId && activeTenant) {
      console.log('--- NUCLEAR USEMATCHES V2 ACTIVATED ---')
      fetchMatches()

      // Realtime Subscription
      const channel = supabase
        .channel(`matches_live_${tournamentId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'matches',
          filter: `tournament_id=eq.${tournamentId}`
        }, () => {
          console.log('⚡ Realtime Match Update Detected! Refetching...')
          fetchMatches()
        })
        .subscribe()

      return () => {
        supabase.removeChannel(channel)
      }
    }
  }, [tournamentId, activeTenant])

  const fetchMatches = async () => {
    try {
      // NUCLEAR OPTION: Manual Join to avoid 400 Bad Request on FKs

      // 1. Fetch plain matches
      const { data: matchData, error: matchError } = await supabase
        .from('matches')
        .select('*')
        .eq('tournament_id', tournamentId)

      if (matchError) throw matchError

      // 2. Fetch teams for lookup
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select('id, name, logo_url')
        .eq('tournament_id', tournamentId)

      if (teamError) throw teamError

      // 3. Fetch poules for lookup
      const { data: pouleData, error: pouleError } = await supabase
        .from('poules')
        .select('id, name')
        .eq('tournament_id', tournamentId)

      // 4. Helper to find team
      const findTeam = (id) => teamData.find(t => t.id === id) || null
      const findPoule = (id) => pouleData?.find(p => p.id === id) || null

      // 5. Merge data in JS
      const enrichedMatches = matchData.map(match => ({
        ...match,
        home_team: findTeam(match.home_team_id),
        away_team: findTeam(match.away_team_id),
        poule: findPoule(match.poule_id)
      }))

      setMatches(enrichedMatches)

    } catch (err) {
      console.error('Error fetching matches (Manual Join):', err)
    } finally {
      setLoading(false)
    }
  }

  const createMatch = async (matchData) => {
    // Ensure we send valid UUIDs or null, not empty strings
    const cleanData = { ...matchData }
    if (!cleanData.home_team_id || cleanData.home_team_id === '') cleanData.home_team_id = null
    if (!cleanData.away_team_id || cleanData.away_team_id === '') cleanData.away_team_id = null
    if (cleanData.match_date === '') cleanData.match_date = null
    if (cleanData.match_time === '') cleanData.match_time = null
    if (cleanData.start_time === '') cleanData.start_time = null

    const { data, error } = await supabase
      .from('matches')
      .insert([{
        ...cleanData,
        tournament_id: tournamentId,
        tenant_id: activeTenant.id
      }])
      .select()
      .single()

    if (error) throw error
    await fetchMatches()
    return data
  }

  const updateMatch = async (matchId, updates) => {
    const cleanUpdates = { ...updates }
    if (cleanUpdates.home_team_id === '') cleanUpdates.home_team_id = null
    if (cleanUpdates.away_team_id === '') cleanUpdates.away_team_id = null
    if (cleanUpdates.match_date === '') cleanUpdates.match_date = null
    if (cleanUpdates.match_time === '') cleanUpdates.match_time = null
    if (cleanUpdates.start_time === '') cleanUpdates.start_time = null

    if (cleanUpdates.score_home !== undefined && cleanUpdates.score_away !== undefined) {
      cleanUpdates.status = (cleanUpdates.score_home !== null && cleanUpdates.score_away !== null) ? 'finished' : 'planned'
    }

    const { error } = await supabase
      .from('matches')
      .update(cleanUpdates)
      .eq('id', matchId)

    if (error) throw error

    // Run promotion logic immediately
    const { data: currentMatchData, error: fetchErr } = await supabase
      .from('matches')
      .select('*')
      .eq('tournament_id', tournamentId)

    if (!fetchErr && currentMatchData) {
      const updatedMatch = currentMatchData.find(m => m.id === matchId)
      if (updatedMatch && updatedMatch.score_home !== null && updatedMatch.score_away !== null) {
        await promoteWinner(matchId, updatedMatch.score_home, updatedMatch.score_away, currentMatchData)
      }
    }

    await fetchMatches()
  }

  // Helper to sort matches for promotion logic (Index > Name)
  const sortMatchesForLogic = (a, b) => {
    if ((a.match_index ?? 999) !== (b.match_index ?? 999)) {
      return (a.match_index ?? 999) - (b.match_index ?? 999)
    }
    return (a.round_name || '').localeCompare(b.round_name || '', undefined, { numeric: true })
  }

  const promoteWinner = async (matchId, scoreHome, scoreAway, allMatches) => {
    try {
      console.log(`[PROMOTE TRACE] Starting... matchId: ${matchId}, scoreHome: ${scoreHome}, scoreAway: ${scoreAway}`);
      const match = allMatches.find(m => m.id === matchId)
      if (!match) { console.log('[PROMOTE TRACE] Match not found in allMatches'); return; }

      // Determine winner ID
      let winnerId = null
      if (scoreHome !== null && scoreAway !== null) {
        if (scoreHome > scoreAway) winnerId = match.home_team_id
        else if (scoreAway > scoreHome) winnerId = match.away_team_id
      }

      console.log(`[PROMOTE TRACE] winnerId: ${winnerId}, match.home: ${match.home_team_id}, match.away: ${match.away_team_id}`);
      if (!winnerId) { console.log('[PROMOTE TRACE] Aborting because winnerId is null'); return; }

      // If draw or no score, we might want to clear the winner from next round?
      // For now, let's only proceed if we have a winner. 
      // If winnerId is null (draw/reset), we arguably should clear the spot.
      // But let's stick to positive promotion for now to avoid accidental clears.
      if (!winnerId) return

      // 1. Determine next round type by parsing "Ronde van X" or fixed names
      const getNextRoundName = (currentRoundName) => {
        if (!currentRoundName) return null
        const lower = currentRoundName.toLowerCase()

        if (lower.includes('finale') && !lower.includes('halve') && !lower.includes('semi') && !lower.includes('kwart') && !lower.includes('quarter') && !lower.includes('achtste') && !lower.includes('zestiende') && !lower.includes('1/')) return null
        if (lower.includes('halve') || lower.includes('semi')) return 'Finale'
        if (lower.includes('kwart') || lower.includes('quarter') || lower.includes('last 8') || lower.includes('1/4')) return 'Halve Finale'
        if (lower.includes('achtste') || lower.includes('last 16') || lower.includes('1/8')) return 'Kwartfinale'
        if (lower.includes('zestiende') || lower.includes('last 32') || lower.includes('1/16')) return 'Achtste Finale'

        if (lower.startsWith('ronde van ') || lower.startsWith('round of ')) {
          let sizeStr = lower.replace('ronde van ', '').replace('round of ', '');
          const size = parseInt(sizeStr)
          const nextSize = size / 2
          if (nextSize === 16) return 'Zestiende Finale'
          if (nextSize === 8) return 'Achtste Finale' // Fallback
          return `Ronde van ${nextSize}`
        }
        return null
      }

      let nextType = ''
      // Try to determine based on 'round_name' first (Standard for Generator)
      if (match.round_name) {
        const calculatedNext = getNextRoundName(match.round_name)
        if (calculatedNext) nextType = calculatedNext
      }

      // Fallback for typed matches (Standings Generator)
      if (!nextType) {
        if (match.type === 'quarter_final') nextType = 'semi_final'
        else if (match.type === 'semi_final') nextType = 'final'
      }

      if (!nextType) { console.log(`[PROMOTE TRACE] Aborting: nextType is empty. Round was: ${match.round_name}`); return; }
      console.log(`[PROMOTE TRACE] nextType resolved to: ${nextType}`);

      // 2. Logic: Should we use explicit next_match_index (Random Generator) or Order Based (Standings Generator)?
      let targetMatch = null
      let updateField = null

      if (match.next_match_index !== undefined && match.next_match_index !== null) {
        // --- RANDOM GENERATOR LOGIC (Explicit links) ---
        // Find match in next round with match_index === next_match_index
        targetMatch = allMatches.find(m => {
          if (m.match_index !== match.next_match_index) return false;
          if (m.type !== 'knockout' && m.type !== nextType) return false;

          const mRound = (m.round_name || '').toLowerCase();
          const nt = nextType.toLowerCase();

          if (nt === 'finale') return mRound === 'final' || mRound === 'finale';
          if (nt === 'halve finale') return mRound.includes('halve') || mRound.includes('semi');
          if (nt === 'kwartfinale') return mRound.includes('kwart') || mRound.includes('quarter');
          if (nt === 'achtste finale') return mRound.includes('achtste') || mRound.includes('16');
          if (nt === 'zestiende finale') return mRound.includes('zestiende') || mRound.includes('32');

          return mRound === nt;
        });

        // Determine slot: Even index -> Home, Odd index -> Away 
        // (This relies on the previous round index)
        const isHomeSlot = (match.match_index % 2) === 0
        updateField = isHomeSlot ? 'home_team_id' : 'away_team_id'

      } else {
        // --- ORDER BASED LOGIC (Standings/General Fallback) ---
        // 1. Get all matches of current round & next round, sorted
        const currentRoundMatches = allMatches.filter(m =>
          (m.type === match.type && m.round_name === match.round_name) ||
          (m.type === match.type && match.type !== 'knockout')
        ).sort(sortMatchesForLogic)

        const nextRoundMatches = allMatches.filter(m => {
          if (m.type === nextType) return true;
          if (m.type !== 'knockout') return false;

          const mRound = (m.round_name || '').toLowerCase();
          const nt = nextType.toLowerCase();

          if (nt === 'finale') return mRound === 'final' || mRound === 'finale';
          if (nt === 'halve finale') return mRound.includes('halve') || mRound.includes('semi');
          if (nt === 'kwartfinale') return mRound.includes('kwart') || mRound.includes('quarter');
          if (nt === 'achtste finale') return mRound.includes('achtste') || mRound.includes('16');
          if (nt === 'zestiende finale') return mRound.includes('zestiende') || mRound.includes('32');

          return mRound === nt;
        }).sort(sortMatchesForLogic)

        // 2. Find index
        const currentIndex = currentRoundMatches.findIndex(m => m.id === matchId)
        if (currentIndex === -1) return

        // 3. Calculate target
        const targetIndex = Math.floor(currentIndex / 2)
        const isHomeSlot = currentIndex % 2 === 0

        targetMatch = nextRoundMatches[targetIndex]
        updateField = isHomeSlot ? 'home_team_id' : 'away_team_id'
      }

      console.log(`[PROMOTE TRACE] targetMatch: ${targetMatch ? targetMatch.id : 'null'} (match_index: ${targetMatch?.match_index}), updateField: ${updateField}`);

      if (targetMatch && updateField) {
        console.log(`[PROMOTE TRACE] current target field value: ${targetMatch[updateField]}, winnerId: ${winnerId}`)
        // Only update if changed
        if (targetMatch[updateField] !== winnerId) {
          console.log(`🚀 Auto-Promoting Winner ${winnerId} to ${nextType} #${targetMatch.match_index || '?'} (${updateField})`)
          await supabase.from('matches').update({ [updateField]: winnerId }).eq('id', targetMatch.id)
        } else {
          console.log(`[PROMOTE TRACE] Aborting update because targetMatch already has winnerId`);
        }
      } else if (!targetMatch && nextType) {
        // Auto-Create Next Round Match if missing!
        console.log(`✨ Auto-Creating Next Round Match: ${nextType}`)

        // 1. Calculate safe index
        let targetIdx = 0
        let isHome = true

        if (match.next_match_index !== undefined && match.next_match_index !== null) {
          targetIdx = match.next_match_index
          // We need to guess home/away based on previous index if possible, or just default to available slot?
          // Standard logic: even index -> home, odd -> away
          isHome = (match.match_index % 2) === 0
        } else {
          // Order logic
          const currentRoundMatches = allMatches.filter(m =>
            (m.type === match.type && m.round_name === match.round_name)
          ).sort(sortMatchesForLogic)

          const currentIndex = currentRoundMatches.findIndex(m => m.id === matchId)
          if (currentIndex !== -1) {
            targetIdx = Math.floor(currentIndex / 2)
            isHome = (currentIndex % 2) === 0
          }
        }

        // 2. Create it
        const newMatchData = {
          tournament_id: match.tournament_id,
          tenant_id: match.tenant_id,
          type: 'knockout',
          round_name: nextType,
          match_index: targetIdx,
          next_match_index: nextType === 'Finale' ? null : Math.floor(targetIdx / 2),
          home_team_id: isHome ? winnerId : null,
          away_team_id: !isHome ? winnerId : null
        }

        const { data: createdMatch, error: createError } = await supabase.from('matches').insert(newMatchData).select().single()

        if (createError) console.error("Auto-Create Error:", createError)
        else console.log("✨ Created:", createdMatch)
      }

    } catch (err) {
      console.error("Promotion Error:", err)
    }
  }

  const updateScore = async (matchId, scoreHome, scoreAway) => {
    // Sanitize values
    const cleanHome = (scoreHome === '' || scoreHome === null || isNaN(scoreHome)) ? null : parseInt(scoreHome);
    const cleanAway = (scoreAway === '' || scoreAway === null || isNaN(scoreAway)) ? null : parseInt(scoreAway);

    // Update Optimistically
    const newMatches = matches.map(m => {
      if (m.id === matchId) {
        return { ...m, score_home: cleanHome, score_away: cleanAway }
      }
      return m
    })
    setMatches(newMatches)

    console.log(`📡 Sending Score Update for ${matchId}:`, { cleanHome, cleanAway })

    const { error } = await supabase
      .from('matches')
      .update({
        score_home: cleanHome,
        score_away: cleanAway,
        status: (cleanHome !== null && cleanAway !== null) ? 'finished' : 'planned'
      })
      .eq('id', matchId)

    if (error) {
      console.error('Score update failed', error)
      await fetchMatches() // Reload truth
      throw error
    } else {
      // Success! Play sound & Try Promotion
      soundFx.play('success')

      // We pass the Optimistic 'newMatches' to promotion logic so it sees the latest scores!
      // Actually, promotion needs to know the Winner. The winner is derived from scores.
      // So passing 'matches' (which is stable) vs 'newMatches' (optimistic).
      // 'newMatches' has the updated score for THIS match, which is what we need to determine the winner.
      await promoteWinner(matchId, cleanHome, cleanAway, newMatches)

      // Finally refetch to get the promoted state confirmed
      await fetchMatches()
    }
  }

  const deleteMatch = async (matchId) => {
    const { error } = await supabase
      .from('matches')
      .delete()
      .eq('id', matchId)

    if (error) throw error
    setMatches(matches.filter(m => m.id !== matchId))
  }

  return { matches, setMatches, loading, createMatch, updateMatch, updateScore, deleteMatch, refetch: fetchMatches }
}
